// src/components/Reports/LedgerSearch.tsx
import React, { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, TextField, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HomeIcon from "@mui/icons-material/Home";
import { useRecentInvoiceAndReturns } from "../../../hooks/useRecentInvoiceAndReturns";
import { InvoiceAndReturnsTable } from "./InvoiceAndReturnsTable";
import { exportInvoiceAndReturnsToExcel } from "../../../utils/exportInvoiceAndReturnsToExcel";
import { exportInvoiceAndReturnsToPdf } from "../../../utils/exportInvoiceAndReturnsToPdf";

export const InvoiceAndReturnsSearch = () => {
	const navigate = useNavigate();
	const { ledgerEntries, loading, fetchLedgerData } =
		useRecentInvoiceAndReturns();
	const currentDate = new Date().toLocaleDateString("en-CA");
	const [startDate, setStartDate] = useState<string>(currentDate);
	const [endDate, setEndDate] = useState<string>(currentDate);

	useEffect(() => {
		fetchLedgerData(startDate, endDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSearch = () => {
		if (!startDate || !endDate) {
			alert("Please select both start and end dates.");
			return;
		}
		if (startDate > endDate) {
			alert("Start date cannot be after end date.");
			return;
		}
		fetchLedgerData(startDate, endDate);
	};

	const handleExportExcel = async () => {
		if (ledgerEntries.length === 0) return;

		await exportInvoiceAndReturnsToExcel({
			ledgerEntries,
			startDate,
			endDate
		});
	};

	const handleExportPdf = () => {
		if (ledgerEntries.length === 0) return;

		exportInvoiceAndReturnsToPdf({
			ledgerEntries,
			startDate,
			endDate
		});
	};

	const renderSearch = (): JSX.Element => {
		return (
			<Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mt: 0 }}>
				<div
					style={{
						display: "flex",
						gap: "1rem",
						marginBottom: "1rem",
						alignItems: "center",
						flexWrap: "nowrap"
					}}
				>
					<TextField
						label="Start Date"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						size="small"
						fullWidth={true}
					/>
					<TextField
						label="End Date"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth={true}
						size="small"
					/>
					<Button
						variant="contained"
						size="medium"
						onClick={handleSearch}
					>
						Search
					</Button>
					<Button
						variant="outlined"
						size="medium"
						onClick={handleExportExcel}
						disabled={loading || ledgerEntries.length === 0}
						startIcon={<FileDownloadIcon />}
					>
						Excel
					</Button>
					<Button
						variant="outlined"
						size="medium"
						onClick={handleExportPdf}
						disabled={loading || ledgerEntries.length === 0}
						startIcon={<PictureAsPdfIcon />}
					>
						PDF
					</Button>
				</div>
			</Paper>
		);
	};

	if (loading) return <p>Searching Sales Settlement Report...</p>;

	return (
		<Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mt: 0 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
					gap: 1
				}}
			>
				<Typography variant="h5">Sales Settlement Report</Typography>
				<Button
					variant="contained"
					startIcon={<HomeIcon />}
					onClick={() => navigate("/")}
				>
					Go to Home
				</Button>
			</Box>
			{renderSearch()}
			<InvoiceAndReturnsTable ledgerEntries={ledgerEntries} />
		</Paper>
	);
};
